import { motion } from "framer-motion";
import heroImg from "../assets/heroi.svg";
import Typed from "typed.js";
import { useEffect, useRef, useState } from "react";
import SparkleParticles from "../components/ui/SparkleParticles";
import { gsap } from 'gsap';

type HoverPopupTextProps = {
  text: string;
  popupContent: React.ReactNode;
};
      
const HoverPopupText: React.FC<HoverPopupTextProps> = ({ text, popupContent }) => {
  
  const revealRef = useRef<HTMLDivElement>(null);
  const containerSpanRef = useRef<HTMLSpanElement>(null);
  const shakeTimeline = useRef<gsap.core.Timeline | null>(null);

  const handleMouseEnter = () => {
    gsap.killTweensOf(revealRef.current);
    gsap.set(revealRef.current, { display: 'block' });

    gsap.to(revealRef.current, {
      opacity: 1,
      x: 0,
      duration: 0.1,
      ease: 'power2.out',
      onComplete: () => {
        shakeTimeline.current = gsap.timeline();
        shakeTimeline.current.to(revealRef.current, {
          x: 0,
          y: 20,
          duration: 0.5,
          repeat: 10,
          rotation: -20,
          yoyo: true,
          ease: 'power1.inOut',
        });
      },
    });
  };

  const handleMouseLeave = () => {
    shakeTimeline.current?.kill();
    shakeTimeline.current = null;

    gsap.to(revealRef.current, {
      opacity: 0,
      x: 20,
      duration: 0.4,
      ease: 'power3.in',
      delay: 0.3,
      onComplete: () => {
        gsap.set(revealRef.current, { display: 'none' });
      },
    });
  };

  useEffect(() => {
    gsap.set(revealRef.current, { opacity: 0, x: 20, display: 'none' });
  }, []);

  return (
    <span className="relative inline-block" ref={containerSpanRef}>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="cursor-pointer text-secondaryGray hover:text-primaryGray transition-colors"
      >
        {text}
      </span>
      <div
        ref={revealRef}
        className="absolute top-0 left-[55vh] px-4 py-2 text-hero-s text-secondaryGray z-10 w-max bg-transparent"
      >
        {popupContent}
      </div>
    </span>
  );
};

const Home = () => {

// Schema
useEffect(() => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';

  script.innerHTML = JSON.stringify([
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "CV Gerbang Ilmu Nusantara (GENIUS)",
      "url": "https://geniusakademi.com",
      "logo": "https://geniusakademi.com/favicon/android-chrome-192x192.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+62-812-46464-0707",
        "contactType": "Customer Service",
        "areaServed": "ID",
        "availableLanguage": ["Indonesian", "English"]
      },
      "sameAs": [
        "https://facebook.com/geniusakademi",
        "https://instagram.com/geniusakademi",
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "url": "https://geniusakademi.com",
      "name": "CV Gerbang Ilmu Nusantara (GENIUS) ",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://geniusakademi.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Penelitian dan Survei",
      "serviceType": "Penelitian dan Survei",
      "provider": {
        "@type": "Organization",
        "name": "CV Gerbang Ilmu Nusantara (GENIUS)"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Publikasi dan Cetak",
      "serviceType": "Publikasi dan Percetakan",
      "provider": {
        "@type": "Organization",
        "name": "CV Gerbang Ilmu Nusantara (GENIUS)"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Pendidikan dan Pelatihan",
      "serviceType": "Kursus, Workshop, Seminar",
      "provider": {
        "@type": "Organization",
        "name": "CV Gerbang Ilmu Nusantara (GENIUS)"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Konsultan Non Kontruksi",
      "serviceType": "Konsultasi Umum, Bisnis, SDM",
      "provider": {
        "@type": "Organization",
        "name": "CV Gerbang Ilmu Nusantara (GENIUS)"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Event Organizer (EO)",
      "serviceType": "Penyelenggaraan Acara & Event",
      "provider": {
        "@type": "Organization",
        "name": "CV Gerbang Ilmu Nusantara (GENIUS)"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Solusi Teknologi",
      "serviceType": "Pengembangan Website, Aplikasi, dan Digitalisasi",
      "provider": {
        "@type": "Organization",
        "name": "CV Gerbang Ilmu Nusantara (GENIUS)"
      }
    }
  ]);

  document.head.appendChild(script);

  return () => {
    document.head.removeChild(script);
  };
}, []);

  const scrollRef = useRef(null);
  const typedElement = useRef<HTMLSpanElement | null>(null);
  const typedInstance = useRef<Typed | null>(null);
  const [showShine, setShowShine] = useState(false);
  const firstString =  ["GERBANG ILMU"];

  // Inisialisasi Typed.js
  useEffect(() => {
    if (!typedElement.current) return;

    typedInstance.current = new Typed(typedElement.current, {
      strings: firstString,
      typeSpeed: 70,
      backSpeed: 20,
      backDelay: 1500,
      startDelay: 300,
      loop: true,
      showCursor: true,
      cursorChar: "|",
    });

    return () => {
      typedInstance.current?.destroy();
    };
  }, []);
  
    useEffect(() => {
      const interval = setInterval(() => {
        setShowShine(true);
        setTimeout(() => setShowShine(false), 800); // Lama shine muncul
      }, 2000); // Muncul setiap 2.5 detik
  
      return () => clearInterval(interval);
    }, []);
      
  return (
    <>
      <section
        id="home"
        className="min-h-screen flex flex-row md:flex-row items-center justify-between px-6 md:px-24 pb-12"
      >    
        {/* Left: Typography */}
        <motion.div
          className="flex-2 md:flex-1 text-left mt-28"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1 }}
        >
          <div className="relative inline-block">
            <h1 className="text-hero-m md:text-hero-lx leading-tight font-inter text-primaryGray space-y-2"
            style={{ fontWeight: 1000 }}>
              <HoverPopupText
                text="MEMBUKA"
                popupContent="Peluang Baru 🚀"
              />
              <br />
              <span className="text-primary min-h-[120px] md:min-h-[80px] inline-block">
                <span ref={typedElement} className="hover:text-darkGreen"></span>
              </span>
              <br />
              <HoverPopupText
                text="MEMBENTUK"
                popupContent="Arah & Masa Depan 🔧"
              />
            </h1>
          </div>

          <div className="relative inline-block text-primary text-hero-m md:text-hero-lx leading-tight font-inter min-h-[60px] bg-[length:200%_auto]" style={{ fontWeight: 1000 }}>
            {/* Teks utama (solid) */}
            <span className="relative z-10 hover:text-darkGreen">MASA DEPAN <br /> GEMILANG</span>

            {/* Shine Overlay */}
            {showShine && (
              <span className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
                <span className="absolute inset-0 animate-shine-effect bg-gradient-to-r from-transparent via-white/60 to-transparent opacity-75 blur-sm dark:hidden" />
                <span className="absolute inset-0 animate-shine-effect bg-gradient-to-r from-transparent via-backgroundDark to-transparent opacity-80 blur-sm hidden dark:block" />
              </span>
            )}
            <SparkleParticles />
          </div>

          <p className="mt-6 md:mt-6 text-gray-500 text-lg max-w-md">
            Dapatkan layanan profesional dari kami — pelatihan, teknologi, hingga publikasi.
          </p>

          <div className="relative inline-block mt-4 text-primary text-[2.8rem] md:text-[4rem] leading-tight font-poppins font-extrabold min-h-[60px] bg-[length:200%_auto]">
    
              {/* Container tombol + mouse */}
              <div className="relative z-10 mt-2 md:mt-8 flex flex-row md:flex-row items-center md:items-center space-x-2 md:space-x-6 gap-2 md:gap-6 ">
                
                <button type="button" className="px-6 py-3 text-white bg-gradient-to-br from-primary to-primaryGray hover:bg-gradient-to-bl 
                  focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-full text-sm text-center">
                  INFO LEBIH LANJUT 
                </button>

                {/* Mouse Animation */}
                <div className="flex justify-center items-center">
                  <div className="w-10 h-16 border-2 border-primaryGray rounded-full flex items-start justify-center">
                    <div className="w-2 h-3 bg-primary/70 rounded-full animate-scroll" />
                  </div>
                </div>
              </div>

              {/* Shine Overlay */}
              {showShine && (
                <span className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
                  <span className="absolute inset-0 animate-shine-effect bg-gradient-to-r from-transparent via-white/60 to-transparent opacity-75 blur-sm dark:hidden" />
                  <span className="absolute inset-0 animate-shine-effect bg-gradient-to-r from-transparent via-backgroundDark to-transparent opacity-70 blur-sm hidden dark:block" />
                </span>
              )}
          </div>

        </motion.div>

        <motion.div
            className="flex-1 mt-10 md:mt-0"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
          >
            {/* Background image untuk mobile */}
            <div
              className="flex hidden md:hidden w-full h-[200px] bg-cover bg-center rounded-xl"
              style={{ backgroundImage: "url('/undraw.svg')" }}
            ></div>

            {/* Gambar biasa untuk tablet ke atas */}
            <img
              src={heroImg}
              alt="Hero Ilustrasi"
              className="
                hidden md:block
                w-[90%] sm:w-[80%] md:w-full
                max-w-md mx-auto
                h-auto
                shadow-[10px_-10px_20px_1px_rgba(0,_0,_0,_0.1)
              "
            />
        </motion.div>
      </section>
    </>
  );
};

export default Home;
